import React from 'react'
import { Pin, PinOff, X } from 'lucide-react'
import type { NavItem, ShellUser } from './types'
import { NavEntry, groupItems } from './NavEntry'

export interface NavDrawerProps {
  items: NavItem[]
  hasMission?: boolean
  moduleName: string
  moduleSubtitle?: string
  logoSrc?: string
  pinned: boolean
  canPin: boolean
  user?: ShellUser
  onClose: () => void
  onTogglePin: () => void
  onNavigate: () => void
  drawerRef: React.Ref<HTMLElement>
  swipeRef?: (el: HTMLElement | null) => void
  closeRef?: React.Ref<HTMLButtonElement>
}

/** Ausgeklapptes Menü mit Text: legt sich als Overlay über den Inhalt oder ist angeheftet (schiebt den Inhalt). */
export function NavDrawer({ items, hasMission, moduleName, moduleSubtitle, logoSrc, pinned, canPin, user, onClose, onTogglePin, onNavigate, drawerRef, swipeRef, closeRef }: NavDrawerProps) {
  const groups = groupItems(items)
  const setRefs = (el: HTMLElement | null) => {
    if (typeof drawerRef === 'function') drawerRef(el)
    else if (drawerRef) (drawerRef as React.MutableRefObject<HTMLElement | null>).current = el
    swipeRef?.(el)
  }
  return (
    <aside id="ui-drawer" className="ui-drawer" aria-label={`Hauptnavigation ${moduleName} (Drawer)`} ref={setRefs}>
      <div className="ui-drawer-head">
        <button type="button" ref={closeRef} className="ui-ib" onClick={onClose} aria-label="Menü schließen"><X aria-hidden="true" /></button>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-[15px] font-semibold">{moduleName}</div>
          {moduleSubtitle && <div className="truncate text-xs text-dark-text-muted">{moduleSubtitle}</div>}
        </div>
        {canPin && (
          <button
            type="button"
            className="ui-ib ui-pin"
            onClick={onTogglePin}
            aria-pressed={pinned}
            aria-label={pinned ? 'Lösen (Menü wieder überlagern)' : 'Angeheftet lassen'}
            title={pinned ? 'Lösen (Menü wieder überlagern)' : 'Angeheftet lassen'}
          >
            {pinned ? <PinOff aria-hidden="true" /> : <Pin aria-hidden="true" />}
          </button>
        )}
      </div>
      <nav className="ui-drawer-list scrollbar-thin" aria-label="Seiten">
        {groups.map((g, gi) => (
          <div key={g.name ?? `g${gi}`} className="ui-grp-wrap">
            {g.name && <div className="ui-grp">{g.name}</div>}
            {g.items.map(it => <NavEntry key={it.path} item={it} variant="drawer" hasMission={hasMission} onNavigate={onNavigate} />)}
          </div>
        ))}
      </nav>
      {user && (
        <div className="ui-drawer-user">
          <span className="ui-avatar" aria-hidden="true">{initials(user.name)}</span>
          <div className="min-w-0 leading-tight">
            <div className="truncate text-sm font-medium">{user.name}</div>
            {user.subtitle && <div className="truncate text-xs text-dark-text-muted">{user.subtitle}</div>}
          </div>
        </div>
      )}
    </aside>
  )
}

export function initials(name: string) {
  const p = name.trim().split(/\s+/).filter(Boolean)
  return ((p[0]?.[0] ?? '') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase() || '?'
}
